<template>
  <v-dialog v-model="showAddSubscriptionDialog" max-width="1200" persistent>
    <v-card v-if="showAddSubscriptionDialog">
      <v-card-title class="primary white--text">
        <h3 class="headline">
          {{ $t('texts.addSubscription') }}
        </h3>
        <v-spacer/>
        <v-btn
          text small fab
          @click="closeDialog()"
        >
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-stepper v-model="step" vertical class="elevation-0">
          <v-stepper-step step="1" complete>
            {{ $t('texts.chooseCompany') }}
          </v-stepper-step>
          <v-stepper-content step="1">
            <form data-vv-scope="form-1">
              <v-card>
                <v-card-text>
                  <v-autocomplete
                    v-model="form.company_id"
                    :label="$t('texts.company.label')"
                    :items="companyItems"
                    item-value="id"
                    item-text="name"
                    v-validate="'required'"
                    data-vv-name="form-1.company_id"
                    :data-vv-as="$t('texts.company.label')"
                    :error-messages="errors.collect('form-1.company_id')"
                    required
                  >
                    <template slot="no-data">
                      <v-list-item :to="{ name: 'add.company' }">
                        <v-list-item-action><v-icon>add</v-icon></v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>{{ $t('texts.addCompany') }}</v-list-item-title>
                          <v-list-item-subtitle>{{ $t('texts.noCompanyWithoutSubscription') }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                  <v-btn
                    outlined text primary
                    :to="{ name: 'add.company' }"
                  >
                    {{ $t('texts.addCompany') }}
                  </v-btn>
                  <v-spacer/>
                  <v-btn
                    color="primary"
                    @click="next()"
                  >
                    {{ $t('texts.next') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </form>
          </v-stepper-content>
          <v-stepper-step step="2" complete>
            {{ $t('texts.chooseSubscription') }}
          </v-stepper-step>
          <v-stepper-content step="2">
            <v-card>
              <form data-vv-scope="form-2">
                <v-card-text>
                  <v-container class="pa-0" grid-list-md>
                    <v-row>
                      <v-col :cols="12" class="text-center">
                        <v-btn
                          @click="setBillingCycle('monthly')"
                          :color="summary.billing_cycle === 'monthly' ? 'primary' : ''"
                          class="mx-0"
                        >
                          {{ $t('texts.perMonth') }}
                        </v-btn>
                        <v-btn
                          @click="setBillingCycle('yearly')"
                          :color="summary.billing_cycle === 'yearly' ? 'primary' : ''"
                          class="mx-0"
                        >
                          {{ $t('texts.perYear') }}
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col
                        :cols="3"
                        v-for="(product, index) in products" :key="index">
                        <v-card>
                          <v-card-title class="justify-center">
                            {{ product.translations.name[language] }}
                          </v-card-title>
                          <v-card-text class="text-center">
                            <h3>CHF {{ product.plans[summary.billing_cycle].amount }}</h3>
                            <p>{{ product.translations.description[language] }}</p>
                            <v-btn
                              color="primary"
                              @click="setSubscription(product)"
                              :outlined="form.stripe_id !== product.plans[summary.billing_cycle].stripe_id"
                            >
                              {{ $t('texts.choose') }}
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                  <v-btn
                    color="primary"
                    @click.native="step = step -= 1"
                    text
                  >
                    {{ $t('texts.back') }}
                  </v-btn>
                </v-card-actions>
              </form>
            </v-card>
          </v-stepper-content>
          <v-stepper-step step="3" complete>
            {{ $t('texts.creditCard') }}
          </v-stepper-step>
          <v-stepper-content step="3">
            <form data-vv-scope="form-3">
              <v-card>
                <v-card-text v-if="stripeEnvIsTesting">
                  <v-alert :value="true" type="info">
                    Test Credit Card Switzerland: 4000007560000009 12/20 123<br>
                    More: <a href="https://stripe.com/docs/testing" style="color: white" target="_blank">https://stripe.com/docs/testing</a>
                  </v-alert>
                </v-card-text>
                <v-card-text>
                  <card class='stripe-card'
                    :class='{ complete }'
                    :stripe='stripePublishableKey'
                    :options='stripeOptions'
                    ref='card'
                    @change='complete = $event.complete'
                  />
                  <span
                  v-show="this.cardError"
                  class="help is-danger error--text input-group__error"
                >
                  {{ this.cardError }}
                </span>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                  <v-btn
                    color="primary"
                    @click.native="step = step -= 1"
                    text
                  >
                    {{ $t('texts.back') }}
                  </v-btn>
                  <v-spacer/>
                  <v-btn
                    color="secondary"
                    @click="getCardToken()"
                  >
                    {{ $t('texts.next') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </form>
          </v-stepper-content>
          <v-stepper-step step="4" complete>
            {{ $t('texts.summary') }}
          </v-stepper-step>
          <v-stepper-content step="4">
            <v-card>
              <v-card-text>
                <form>
                  <v-container>
                    <v-row>
                      <v-col :cols="12" :sm="4">
                        {{ $t('texts.company.label') }}
                      </v-col>
                      <v-col :cols="12" :sm="8">
                        {{ selectedCompany.name }}
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col :cols="12" :sm="4">
                        {{ $t('texts.subscription') }}
                      </v-col>
                      <v-col :cols="12" :sm="8">
                        {{ summary.name }}
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col :cols="12" :sm="4">
                        <template v-if="summary.billing_cycle === 'monhtly'">
                          {{ $t('texts.pricePerMonth') }}
                        </template>
                        <template v-if="summary.billing_cycle === 'yearly'">
                          {{ $t('texts.pricePerYear') }}
                        </template>
                      </v-col>
                      <v-col :cols="12" :sm="8">
                        CHF {{ summary.amount }}
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col :cols="12" :sm="4">
                        {{ $t('texts.creditCardEndsWith') }}
                      </v-col>
                      <v-col :cols="12" :sm="8">
                        {{ summary.last4 }}
                      </v-col>
                    </v-row>
                    <v-row v-if="Object.keys(summary.coupon).length === 0">
                      <v-col :cols="12" :sm="4"></v-col>
                      <v-col :cols="12" :sm="8">
                        <v-text-field
                          v-model="couponCheck.code"
                          :label="$t('texts.coupon')"
                          :error="!couponCheck.valid && couponCheck.valid !== null"
                          solo
                          @blur="validateCouponCode()"
                          append-icon="check_circle_outline"
                          @click:append="validateCouponCode"
                          :append-outer-icon="couponCheck.valid === false ? 'error' : ''"
                          :loading="couponCheck.loading"
                          clearable
                          @click:clear="clearCouponCode"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="Object.keys(summary.coupon).length > 0">
                      <v-col :cols="12" :sm="4">
                        {{ $t('texts.coupon') }}
                      </v-col>
                      <v-col :cols="12" :sm="8">
                        <v-card>
                          <v-card-text>
                            <v-container class="pa-0">
                              <v-row>
                                <v-col :cols="12" :sm="4">{{ $t('texts.label') }}</v-col>
                                <v-col :cols="12" :sm="8">{{ summary.coupon.name }}</v-col>
                              </v-row>
                              <v-row>
                                <v-col :cols="12" :sm="4">{{ $t('texts.value') }}</v-col>
                                <v-col :cols="12" :sm="8">
                                  <template v-if="summary.coupon.amount_off !== 0">
                                    CHF {{ summary.coupon.amount_off }}.-
                                  </template>
                                  <template v-else>
                                    {{ summary.coupon.percent_off }}%
                                  </template>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col :cols="12" :sm="4">{{ $t('texts.duration') }}</v-col>
                                <v-col :cols="12" :sm="8">
                                  {{ summary.coupon.duration_in_months }}
                                  {{ $tc('texts.months.label', summary.coupon.duration_in_months) }}
                                </v-col>
                              </v-row>
                            </v-container>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col :cols="1">
                        <v-checkbox
                          v-model="summary.confirmation"
                          :true-value="1"
                          :false-value="0"
                          color="primary"
                          hide-details
                          required
                          v-validate="'required|included:1'"
                          data-vv-name="confirmation"
                          :data-vv-as="$t('texts.confirmSubscriptionSelection')"
                          :error-messages="errors.collect('confirmation')"
                          class="mt-0"
                        ></v-checkbox>
                      </v-col>
                      <v-col :cols="11" d-flex align-center>
                        <span
                          :class="{
                            'error--text':
                            errors.collect('confirmation').length > 0
                          }"
                          v-html="$t('texts.confirmSubscriptionSelection')">
                        </span>
                      </v-col>
                    </v-row>
                  </v-container>
                </form>
              </v-card-text>
              <v-divider/>
              <v-card-actions>
                <v-btn
                  color="primary"
                  @click.native="step = step -= 1"
                  text
                >
                  {{ $t('texts.back') }}
                </v-btn>
                <v-spacer/>
                <v-btn
                  color="primary"
                  @click="addSubscription()"
                  :disabled="buttonLoading ||
                  couponCheck.loading ||
                  couponCheck.valid === false ||
                  (couponCheck.code !== null && couponCheck.code !== '')"
                  :loading="buttonLoading"
                >
                  {{ $t('texts.buySubscription') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { Card, createToken } from 'vue-stripe-elements-plus';
import { stripePublishableKey } from '@/config';

const { mapState: mapUserState } = createNamespacedHelpers('user');
const { mapState: mapCompanyState } = createNamespacedHelpers('company');
const { mapState: mapSubscriptionState, mapActions: mapSubscriptionActions } = createNamespacedHelpers('subscription');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'AddSubscriptionDialog',
  components: {
    Card,
  },
  props: {
    products: {
      type: Array,
    },
  },
  data() {
    return {
      showAddSubscriptionDialog: false,
      buttonLoading: false,
      stripePublishableKey,
      complete: false,
      stripeOptions: {
        style: {
          base: {
            fontSize: '16px',
          },
        },
      },
      step: 1,
      couponCheck: {
        code: '',
        loading: false,
        valid: null,
      },
      form: {
        stripe_id: null,
        company_id: null,
        credit_card_token: '',
        coupon_code: '',
      },
      summary: {
        company_name: '',
        name: '',
        billing_cycle: 'monthly',
        amount: '',
        last4: '',
        coupon: {},
        confirmation: 0,
      },
      cardError: null,
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  mounted() {
    this.$eventBus.$on('show-add-subscription-dialog', () => {
      this.reset();
      this.showAddSubscriptionDialog = true;
    });
  },
  computed: {
    ...mapCompanyState([
      'companies',
    ]),
    ...mapUserState([
      'language',
    ]),
    ...mapSubscriptionState([
      'checkCouponCodePending',
    ]),
    companyItems() {
      return this.companies.filter((company) => !company.active_subscription);
    },
    selectedCompany() {
      if (this.form.company_id !== null) {
        const selectedCompany = this.companies.filter((company) => company.id === this.form.company_id);
        return selectedCompany[0];
      }
      return {};
    },
    stripeEnvIsTesting() {
      return this.stripePublishableKey.substr(0, 7) === 'pk_test';
    },
  },
  methods: {
    ...mapSubscriptionActions([
      'checkCouponCode',
      'storeSubscription',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    next() {
      const scope = `form-${this.step}`;
      this.$validator.validateAll(scope).then((result) => {
        if (result) {
          if (this.step === 3) {
            this.step += 1;
          } else {
            this.step += 1;
          }
        } else {
          this.showMessageValidateError();
        }
      });
    },
    setBillingCycle(value) {
      this.summary.billing_cycle = value;
    },
    setSubscription(product) {
      this.form.stripe_id = product.plans[this.summary.billing_cycle].stripe_id;
      this.summary.name = product.translations.name.de;
      this.summary.amount = product.plans[this.summary.billing_cycle].amount;
      this.next();
    },
    getCardToken() {
      createToken().then((data) => {
        if (data.error) {
          this.cardError = this.$i18n.t('texts.creditcardInvalid');
        } else {
          this.cardError = null;
          this.form.credit_card_token = data.token.id;
          this.summary.last4 = data.token.card.last4;
          if (this.form.credit_card_token !== '') {
            this.next();
          }
        }
      });
    },
    validateCouponCode() {
      if (this.couponCheck.code !== '' && this.couponCheck.code !== null) {
        this.couponCheck.loading = true;
        this.couponCheck.valid = null;
        this.checkCouponCode({
          coupon_code: this.couponCheck.code,
        }).then((response) => {
          this.couponCheck.loading = false;
          this.summary.coupon = response.data.data;
          this.form.coupon_code = this.couponCheck.code;
          this.couponCheck.valid = true;
          this.couponCheck.code = '';
        }).catch(() => {
          this.couponCheck.loading = false;
          this.couponCheck.valid = false;
        });
      }
    },
    clearCouponCode() {
      this.couponCheck.code = '';
      this.couponCheck.loading = false;
      this.couponCheck.valid = null;
    },
    reset() {
      this.buttonLoading = false;
      this.step = 1;
      this.couponCheck = {
        code: '',
        loading: false,
        valid: null,
      };
      this.form = {
        stripe_id: null,
        company_id: null,
        credit_card_token: '',
        coupon_code: '',
      };
      this.summary = {
        company_name: '',
        name: '',
        billing_cycle: 'monthly',
        amount: '',
        last4: '',
        coupon: {},
        confirmation: 0,
      };
      // Reset stripe card
      this.$validator.reset();
    },
    closeDialog() {
      this.reset();
      this.$refs.card.clear();
      this.cardError = null;
      this.showAddSubscriptionDialog = false;
    },
    addSubscription() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.buttonLoading = true;
          this.storeSubscription(this.form)
            .then(() => {
              this.showAddSubscriptionDialog = false;
              this.$refs.card.clear();
              this.reset();
            });
        } else {
          this.showMessageValidateError();
        }
      });
    },
  },
};
</script>
